import { useEffect, useRef, useState, type FormEvent } from "react";

import Icon from "@/components/icon";
import { Button } from "@/components/shadcn/button";
import { Command, CommandInput } from "@/components/shadcn/command";
import { DrugSuggestionDropdown } from "@/components/search/drug-suggestion-dropdown";
import {
  buildFlatRows,
  hasAtcToken,
  isCommittableRow,
  isFuzzyOnly,
} from "@/components/search/suggestion-rows";
import { useDrugLabel } from "@/hooks/use-drug-label";
import { useDrugSuggestions } from "@/hooks/use-drug-suggestions";
import { useMediaQuery } from "@/hooks/use-media-query";
import { plForm } from "@/lib/pl-plural";
import { shouldAdoptResolvedLabel } from "@/routes/search-label";

// The always-mounted search field for the home/search page. It stays the same
// component instance across the empty-home → loading → report transitions
// (only the wrapper styling swaps via `docked`), so focus and layout never
// jump. Mounted only under the auth gate — suggestions assume a signed-in user.
//
// A running search never locks the box: suggestions, the clear button and
// submit all stay live, so a doctor can start another lookup instead of
// refreshing the page when generation takes minutes. Re-submitting the same
// query does not start a second search (the page keys searches by URL; only a
// failed search is retried).
interface SearchBoxProps {
  // The decoded /:query URL segment; "" on the bare homepage.
  decodedQuery: string;
  // Human label riding along on a warm in-app navigation (a typeahead pick).
  stateLabel: string | null;
  // Visual only (spinner + "Szukam…" on the submit button); never gates input.
  isLoading: boolean;
  // True while a search/report owns the screen: renders as the sticky top bar.
  docked: boolean;
  // The human label currently shown in the box for the active search (e.g.
  // "Paracetamolum"); null after a clear. The page folds non-empty values into
  // the report title and history label (nextTitleLabel). SearchBox keeps a
  // local mirror (committedBoxLabel) for its own guards — the prop arrives a
  // commit after the box text it describes, which is too late for them.
  committedLabel: string | null;
  onCommittedLabelChange: (label: string | null) => void;
  // Commit a search: show `label` in the input, but search by `query` (an ATC
  // code when known — the cache convergence key — else a substance name).
  onSearch: (query: string, label?: string) => void;
}

export function SearchBox({
  decodedQuery,
  stateLabel,
  isLoading,
  docked,
  onCommittedLabelChange,
  onSearch,
}: SearchBoxProps) {
  // Starts dismissed on a cold link (a URL already owns the box); typing or a
  // fresh query re-arms the dropdown via the render-time resets below.
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(() =>
    Boolean(decodedQuery),
  );
  // cmdk's active item, tagged with the list it was picked from. cmdk keeps
  // its active value when the rows swap under it (fresh suggestions landing
  // after the debounce), so "r3" could point at nothing and Enter would go
  // dead; `commandValue` below ignores a pick from another list. Derived, not
  // reset with a render-phase setState: React drops such a reset while a
  // sync echo from cmdk is still pending.
  const [selection, setSelection] = useState({ listKey: "", value: "" });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // The blur dismiss runs on a delay so a click inside the dropdown lands first;
  // the refs let a refocus (or a focus move INTO the panel) cancel it.
  const blurTimerRef = useRef<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // A plain controlled input: no schema validation — the disabled submit
  // button already gates queries under 2 chars, so a min-length rule was
  // unreachable.
  //
  // The URL is the source of truth for what the box shows. A suggestion passes
  // a human label while the URL carries the ATC code (the cache-convergence
  // key), so a warm nav shows that label; a cold ATC link starts blank (the
  // reconstructed Latin label lands via the adoption reset below) rather than
  // flashing the raw code; a plain name/free-text query shows verbatim. Back
  // on "/" the box resets.
  const urlBoxLabel = decodedQuery
    ? (stateLabel ?? (hasAtcToken(decodedQuery) ? "" : decodedQuery))
    : "";
  const [searchQuery, setSearchQuery] = useState(urlBoxLabel);
  // Local mirror of the label the box is committed to (the URL's label, the
  // adopted reconstruction, or null after a clear). The parent's committedLabel
  // lags by a commit — it is notified in an effect below — so the guards read
  // this mirror and never race that round trip.
  const [committedBoxLabel, setCommittedBoxLabel] = useState<string | null>(
    decodedQuery ? urlBoxLabel : null,
  );

  // True while the box still shows the label of the search we're already viewing
  // (the user hasn't edited it) — suppresses the pointless self-lookup dropdown.
  const showingCommitted =
    committedBoxLabel !== null &&
    searchQuery.trim() === committedBoxLabel.trim();
  const {
    groups,
    loading: suggestionsLoading,
    error: suggestionsError,
    stale: suggestionsStale,
    retry: retrySuggestions,
  } = useDrugSuggestions(searchQuery, !showingCommitted);
  // The human (Latin substance) label for the current URL: the picked label on a
  // warm nav, else reconstructed from the ATC code(s) so a shared/refreshed link
  // shows "Paracetamolum", not "N02BE01".
  const resolvedLabel = useDrugLabel(
    decodedQuery,
    stateLabel,
    Boolean(decodedQuery),
  );

  // Dropdown dismissal and box sync as derived state (render-time resets,
  // never a synchronous setState in an effect): a committed search (URL
  // change) dismisses the dropdown and fills the box from the URL; any edit
  // to the box text re-arms the dropdown.
  const commitKey = `${decodedQuery} ${stateLabel ?? ""}`;
  const [trackedCommitKey, setTrackedCommitKey] = useState(commitKey);
  if (commitKey !== trackedCommitKey) {
    setTrackedCommitKey(commitKey);
    setSuggestionsDismissed(true);
    setSearchQuery(urlBoxLabel);
    setCommittedBoxLabel(decodedQuery ? urlBoxLabel : null);
  }
  const [trackedSearchQuery, setTrackedSearchQuery] = useState(searchQuery);
  if (searchQuery !== trackedSearchQuery) {
    setTrackedSearchQuery(searchQuery);
    setSuggestionsDismissed(false);
    if (
      shouldAdoptResolvedLabel(resolvedLabel, committedBoxLabel, searchQuery)
    ) {
      setSearchQuery(resolvedLabel);
      setCommittedBoxLabel(resolvedLabel);
    }
  }
  // Adopt the reconstructed (Latin substance) label for an ATC-coded URL once
  // it resolves — but only while the box still shows what we put there, so we
  // never clobber something the user has started typing. committedBoxLabel ===
  // null means the user cleared the box; "" means we're still waiting on a
  // cold ATC link.
  const [trackedResolvedLabel, setTrackedResolvedLabel] =
    useState(resolvedLabel);
  if (resolvedLabel !== trackedResolvedLabel) {
    setTrackedResolvedLabel(resolvedLabel);
    if (
      shouldAdoptResolvedLabel(resolvedLabel, committedBoxLabel, searchQuery)
    ) {
      setSearchQuery(resolvedLabel);
      setCommittedBoxLabel(resolvedLabel);
    }
  }

  // Never leave a pending blur-dismiss behind after unmount.
  useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) {
        window.clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
    };
  }, []);

  // Autofocus the search box on pointer devices only. On touch, programmatic
  // focus pops the keyboard and shoves the hero around — worse than one tap.
  const pointerDevice = useMediaQuery("(hover: hover) and (pointer: fine)");
  useEffect(() => {
    if (pointerDevice && !decodedQuery) {
      searchInputRef.current?.focus();
    }
  }, [pointerDevice, decodedQuery]);

  // Notify the parent of the committed label (it labels history and the result
  // view). The mirror above is the local truth; this effect is the only
  // outbound channel, so the parent never races the render-time resets.
  useEffect(() => {
    onCommittedLabelChange(committedBoxLabel);
  }, [committedBoxLabel, onCommittedLabelChange]);

  // On touch the keyboard stays up after a pick and hides the report that
  // replaces the dropdown; dropping focus closes it.
  const coarsePointer = useMediaQuery("(pointer: coarse)");
  const commit = (...search: Parameters<typeof onSearch>) => {
    onSearch(...search);
    if (coarsePointer) {
      searchInputRef.current?.blur();
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    commit(searchQuery.trim());
  };

  // No silent hide (F1): once the query is searchable the panel stays up to show
  // results, loading, the empty guidance, or the error — whichever applies.
  const showDropdown =
    !suggestionsDismissed &&
    !showingCommitted &&
    searchQuery.trim().length >= 2;

  // Closing on an outside tap/click, not only on blur: a tap on the page
  // chrome need not move focus, and the blur timer would never fire.
  useEffect(() => {
    if (!showDropdown) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        formRef.current?.contains(event.target)
      ) {
        return;
      }
      setSuggestionsDismissed(true);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [showDropdown]);

  // The same row list the dropdown renders, so the announcer and the active
  // item agree with it.
  const rows = buildFlatRows(groups);
  const rowCount = rows.length;
  const committableItems = rows.flatMap((row, i) =>
    isCommittableRow(row.group) ? [{ key: row.key, value: `r${i}` }] : [],
  );
  const loadingFirstRows = suggestionsLoading && groups.length === 0;
  // Rows cmdk may commit on Enter: shown, fresh, and not the error or
  // skeleton panel. Stale rows are disabled, so cmdk holds no selection
  // while they show.
  const rowsLive =
    showDropdown &&
    !suggestionsError &&
    !loadingFirstRows &&
    !suggestionsStale &&
    rowCount > 0;
  // cmdk calls preventDefault() on every Enter inside <Command>, which swallows
  // the form submit. Whenever cmdk has no row to commit — dropdown hidden,
  // skeletons, the error panel, stale rows, or the free-text fallback —
  // Enter must submit the typed text itself; preventing default first makes
  // cmdk skip its own Enter branch. While live rows exist cmdk commits the
  // active one, so this must stay off or the search would fire twice.
  const enterSubmitsFreeText = searchQuery.trim().length >= 2 && !rowsLive;

  // Mirror the items the dropdown renders (same indices, disabled rows
  // skipped); the first one is active unless the user moved within this very
  // list. The stale phase mirrors as an empty list, so fresh rows start from
  // the first one even when they carry the same keys as before.
  const listItems = rowsLive ? committableItems : [];
  const listKey = listItems.map((item) => item.key).join("|");
  const commandValue =
    selection.listKey === listKey &&
    listItems.some((item) => item.value === selection.value)
      ? selection.value
      : (listItems[0]?.value ?? "");

  // cmdk scrolls the active item into view before a controlled value reaches
  // the items, so End/Home could leave it off-screen. Find the row by its
  // cmdk value, which is set on mount and never races.
  useEffect(() => {
    if (!commandValue) return;
    dropdownRef.current
      ?.querySelector(`[cmdk-item][data-value="${commandValue}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [commandValue]);

  const committableCount = committableItems.length;
  const announcement = !showDropdown
    ? ""
    : suggestionsError
      ? "Nie udało się wczytać podpowiedzi."
      : loadingFirstRows || suggestionsStale
        ? "Szukamy podpowiedzi…"
        : rowCount === 0
          ? `Brak dopasowań dla „${searchQuery}”.`
          : committableCount === 0
            ? `Brak pozycji do wyboru dla „${searchQuery}”.`
            : isFuzzyOnly(rows)
              ? `Brak dokładnych dopasowań dla „${searchQuery}”. Znaleziono ${committableCount} ${plForm(committableCount, "podobną pozycję", "podobne pozycje", "podobnych pozycji")}. Użyj strzałek i klawisza Enter, aby wybrać.`
              : `Znaleziono ${committableCount} ${plForm(committableCount, "pozycję", "pozycje", "pozycji")}. Użyj strzałek i klawisza Enter, aby wybrać.`;

  return (
    <div
      className={
        docked
          ? "sticky top-16 z-10 -mx-4 border-b border-border bg-background/85 px-4 py-3 backdrop-blur-md sm:px-6"
          : "scroll-mt-20"
      }
    >
      <form ref={formRef} onSubmit={handleSubmit}>
        <Command
          shouldFilter={false}
          loop
          value={commandValue}
          onValueChange={(value) => setSelection({ listKey, value })}
          className="overflow-visible rounded-none bg-transparent"
        >
          <div className="relative flex h-14 w-full items-center rounded-xl border border-input bg-card shadow-sm transition-[border-color,box-shadow] focus-within:border-brand/50 focus-within:ring-1 focus-within:ring-ring">
            <CommandInput
              ref={searchInputRef}
              placeholder="Lek, substancja lub kod ATC"
              className="h-full min-w-0 flex-1 rounded-xl border-0 bg-transparent pl-5 pr-2 text-lg shadow-none focus-visible:ring-0"
              value={searchQuery}
              onValueChange={(v) => {
                setSearchQuery(v);
                setSuggestionsDismissed(false);
              }}
              onFocus={() => {
                // A refocus cancels a pending blur-dismiss (e.g. the
                // retry button hands focus back to the field).
                if (blurTimerRef.current !== null) {
                  window.clearTimeout(blurTimerRef.current);
                  blurTimerRef.current = null;
                }
                setSuggestionsDismissed(false);
              }}
              onBlur={(e) => {
                // Focus moving INTO the dropdown (Tab to the retry
                // button) must not dismiss the panel.
                if (
                  e.relatedTarget instanceof Node &&
                  dropdownRef.current?.contains(e.relatedTarget)
                ) {
                  return;
                }
                blurTimerRef.current = window.setTimeout(
                  () => setSuggestionsDismissed(true),
                  150,
                );
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape" || event.key === "Tab") {
                  setSuggestionsDismissed(true);
                  return;
                }
                if (event.key === "Enter" && enterSubmitsFreeText) {
                  event.preventDefault();
                  commit(searchQuery.trim());
                }
              }}
            />

            {searchQuery.length > 0 && (
              <button
                type="button"
                aria-label="Wyczyść wyszukiwanie"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  // committedLabel null (not "") is intentional: the
                  // ATC label-restore reset must not put the old
                  // drug name back after a clear.
                  setSearchQuery("");
                  setCommittedBoxLabel(null);
                  onCommittedLabelChange(null);
                  setSuggestionsDismissed(false);
                  searchInputRef.current?.focus();
                }}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Icon name="x" className="h-4 w-4" />
              </button>
            )}

            {/* h-11 in the h-14 field leaves a visible inset on
                every side, and mr-1.5 + rounded-lg keeps the button's
                corner radius concentric with the field's rounded-xl
                — previously it sat nearly flush and read as clipped. */}
            <Button
              type="submit"
              className="mr-1.5 h-11 shrink-0 gap-2 rounded-lg px-4 text-base sm:px-6"
              disabled={searchQuery.trim().length < 2}
              aria-label={isLoading ? "Szukam…" : "Szukaj"}
            >
              {isLoading ? (
                <Icon name="loader-circle" className="animate-spin" />
              ) : (
                <Icon name="search" />
              )}
              <span className="hidden sm:inline">
                {isLoading ? "Szukam…" : "Szukaj"}
              </span>
            </Button>

            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 top-[calc(100%+0.375rem)] z-50 overflow-hidden rounded-xl border border-border bg-card shadow-lg"
                // Keep focus on the input when clicking a row: cmdk
                // commits on click (preventDefault on mousedown
                // doesn't block click), so the onBlur dismiss timer
                // never fires and the dropdown stays put instead of
                // vanishing mid-interaction.
                onMouseDown={(e) => e.preventDefault()}
              >
                <DrugSuggestionDropdown
                  groups={groups}
                  query={searchQuery}
                  loading={suggestionsLoading}
                  error={suggestionsError}
                  stale={suggestionsStale}
                  onCommit={commit}
                  onRetry={() => {
                    retrySuggestions();
                    // Return focus to the field: its onFocus cancels
                    // the blur-dismiss the button click triggered, so
                    // the panel is alive when the refetch lands.
                    searchInputRef.current?.focus();
                  }}
                  onFreeTextSearch={() => commit(searchQuery.trim())}
                />
              </div>
            )}
          </div>
        </Command>
      </form>
      <div aria-live="polite" role="status" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}
